import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { Bell, Crown, Save, Send, Plus, Trash2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const CONTRACTS = ["CDI", "CDD", "Freelance", "Stage", "Alternance"];

export default function AlertSettings() {
  const [prefs, setPrefs] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/alerts/preferences").then((r) => {
      setPrefs(r.data.preferences);
      setIsPremium(r.data.is_premium);
    });
    api.get("/countries").then((r) => setCountries(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/alerts/preferences", prefs);
      if (r.data.downgraded) toast.warning("Fréquence ajustée à 'quotidien' (réservé Premium en temps réel)");
      else toast.success("Préférences enregistrées");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/alerts/test");
      if (r.data.email_status === "sent") toast.success("Email de test envoyé !");
      else if (r.data.email_status === "logged_no_key") toast.info("Mode démo : email loggé côté serveur (clé Resend non configurée)");
      else toast.error("Échec d'envoi");
    } catch { toast.error("Erreur"); } finally { setTesting(false); }
  };

  const toggleContract = (c) => {
    const list = prefs.contract_types || [];
    setPrefs({ ...prefs, contract_types: list.includes(c) ? list.filter((x) => x !== c) : [...list, c] });
  };

  if (!prefs) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div className="space-y-5" data-testid="alert-settings">
      <div className="mec-card p-6 bg-gradient-to-br from-blue-50/80 to-white border-blue-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isPremium ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
              {isPremium ? <Crown className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
            </div>
            <div>
              <div className="font-display font-semibold text-slate-900">{isPremium ? "Compte Premium actif" : "Compte gratuit"}</div>
              <div className="text-xs text-slate-600">{isPremium ? "Alertes en temps réel + badge Premium + boost de visibilité" : "Recevez un récap quotidien des offres correspondantes"}</div>
            </div>
          </div>
          {!isPremium && (
            <Link to="/pricing"><Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white"><Crown className="w-4 h-4 mr-1.5" /> Passer Premium</Button></Link>
          )}
        </div>
      </div>

      <div className="mec-card p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base font-semibold">Activer les alertes emploi</Label>
            <p className="text-xs text-slate-500 mt-0.5">Recevez un email quand de nouvelles offres correspondent à votre profil.</p>
          </div>
          <Switch checked={!!prefs.enabled} onCheckedChange={(v) => setPrefs({ ...prefs, enabled: v })} data-testid="alerts-enabled" />
        </div>

        {prefs.enabled && (
          <>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Fréquence</Label>
              <RadioGroup value={prefs.frequency} onValueChange={(v) => setPrefs({ ...prefs, frequency: v })} className="grid sm:grid-cols-2 gap-3">
                <label className={`mec-card p-4 cursor-pointer ${prefs.frequency === "daily" ? "border-[#004EEB] border-2" : ""}`}>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="daily" id="f-daily" data-testid="freq-daily" />
                    <div>
                      <div className="font-semibold text-sm">Récap quotidien</div>
                      <div className="text-xs text-slate-500 mt-0.5">Une fois par jour, groupé. Disponible pour tous.</div>
                    </div>
                  </div>
                </label>
                <label className={`mec-card p-4 cursor-pointer ${prefs.frequency === "realtime" ? "border-[#004EEB] border-2" : ""} ${!isPremium ? "opacity-60" : ""}`}>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="realtime" id="f-realtime" disabled={!isPremium} data-testid="freq-realtime" />
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-1.5">Temps réel <Crown className="w-3.5 h-3.5 text-amber-500" /></div>
                      <div className="text-xs text-slate-500 mt-0.5">Email immédiat. Réservé Premium.</div>
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-sm font-semibold">Mot-clé personnalisé (optionnel)</Label>
              <Input value={prefs.keyword || ""} onChange={(e) => setPrefs({ ...prefs, keyword: e.target.value })} placeholder='Ex : "data scientist", "marketing", "React"' className="mt-1.5" data-testid="alerts-keyword" />
              <p className="text-xs text-slate-500 mt-1">Filtre strict : seules les offres contenant ce mot-clé déclencheront une alerte.</p>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Pays ciblés (laisser vide = tous)</Label>
              <div className="flex gap-2 mb-2">
                <select value={countryInput} onChange={(e) => setCountryInput(e.target.value)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="alerts-country-select">
                  <option value="">— Sélectionner un pays —</option>
                  {countries.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                </select>
                <Button type="button" variant="outline" onClick={() => {
                  if (countryInput && !(prefs.country_codes || []).includes(countryInput)) {
                    setPrefs({ ...prefs, country_codes: [...(prefs.country_codes || []), countryInput] });
                    setCountryInput("");
                  }
                }}><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(prefs.country_codes || []).map((cc) => {
                  const c = countries.find((x) => x.code === cc);
                  return (
                    <Badge key={cc} variant="outline" className="cursor-pointer hover:bg-red-50 hover:border-red-200" onClick={() => setPrefs({ ...prefs, country_codes: prefs.country_codes.filter((x) => x !== cc) })}>
                      {c?.flag} {c?.name || cc} <Trash2 className="w-3 h-3 ml-1" />
                    </Badge>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Compétences ciblées (laisser vide = vos compétences profil)</Label>
              <div className="flex gap-2 mb-2">
                <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)} placeholder="Python, React, AWS..."
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (skillInput.trim()) { setPrefs({ ...prefs, skills: [...(prefs.skills || []), skillInput.trim()] }); setSkillInput(""); } } }}
                  data-testid="alerts-skill-input" />
                <Button type="button" variant="outline" onClick={() => { if (skillInput.trim()) { setPrefs({ ...prefs, skills: [...(prefs.skills || []), skillInput.trim()] }); setSkillInput(""); } }}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(prefs.skills || []).map((s, i) => (
                  <Badge key={i} variant="outline" className="cursor-pointer hover:bg-red-50" onClick={() => setPrefs({ ...prefs, skills: prefs.skills.filter((_, j) => j !== i) })}>{s} ✕</Badge>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Types de contrat</Label>
              <div className="flex flex-wrap gap-2">
                {CONTRACTS.map((c) => {
                  const on = (prefs.contract_types || []).includes(c);
                  return (
                    <button type="button" key={c} onClick={() => toggleContract(c)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${on ? "bg-[#004EEB] text-white border-[#004EEB]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
                      data-testid={`contract-${c}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={!!prefs.remote_only} onCheckedChange={(v) => setPrefs({ ...prefs, remote_only: v })} data-testid="alerts-remote-only" />
              <Label className="cursor-pointer text-sm">Uniquement offres en télétravail</Label>
            </div>
          </>
        )}

        <div className="flex gap-2 pt-4 border-t border-slate-200">
          <Button onClick={save} disabled={saving} className="bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="save-alerts">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "..." : "Enregistrer"}
          </Button>
          <Button onClick={sendTest} disabled={testing} variant="outline" data-testid="test-alert">
            <Send className="w-4 h-4 mr-1.5" /> {testing ? "..." : "Envoyer un email de test"}
          </Button>
        </div>
      </div>
    </div>
  );
}
